/**
 * Structured, never-throwing application logger.
 *
 * WHY THIS EXISTS
 * ---------------
 * When a guest, an admin, or PYN reports a "dubious problem" weeks later, we
 * need a record of what actually happened. Free-form `console.log` strings are
 * unstructured (can't grep by field), unbounded (no size caps), and inconsistent
 * (no levels). This is the single sanctioned logging path: one structured line
 * per event, with these hard guarantees baked in.
 *
 * GUARANTEES
 * ----------
 *  1. NEVER THROWS. Logging is observational — it must not break the flow it
 *     observes. Every emit is wrapped so a serialization bug, a circular ref, or
 *     a dead stream can never bubble into payment/booking code.
 *  2. CHEAP. Sub-millisecond on the hot path: if the level is below threshold we
 *     return BEFORE serializing anything; otherwise it's a single capped
 *     `JSON.stringify` of one object written to a pipe. No async, no buffering —
 *     synchronous writes survive a crash (the moment you most want the logs).
 *  3. BOUNDED. Every string field is capped (so a giant PYN payload or stack
 *     can't blow up a line), and the whole line is capped. Combined with
 *     journald's own rotation/rate-limiting on the server, logs can't silently
 *     grow into multi-gigabyte files.
 *  4. SAFE. Keys that look like secrets (passwords, tokens, client_secret,
 *     Authorization, api_key, cookies) are redacted before they ever hit a line.
 *  5. ISOMORPHIC. On the server it writes NDJSON to stdout/stderr (captured by
 *     journald). In the browser (client components) it falls back to `console`.
 *
 * SINK
 * ----
 * Always stdout/stderr (captured by journald on the systemd box, by Docker's
 * json-file driver on Coolify). On the systemd box journald owns rotation and
 * retention and that is enough.
 *
 * On Coolify there is a second, OPTIONAL sink: when `LOG_DIR` is set we ALSO
 * append each line to `${LOG_DIR}/app.ndjson`. Docker's per-container json-file
 * log is deleted when Coolify recreates the container on every redeploy, so
 * incident logs vanish the next time we ship. Pointing `LOG_DIR` at a Coolify
 * *persistent volume* keeps a durable, greppable NDJSON history that survives
 * redeploys. The file sink is size-rotated in-process, and — like everything
 * here — can never throw or slow the hot path: any error disables it for the
 * rest of the process. Unset `LOG_DIR` (dev laptop, tests) → this is a no-op.
 *
 * USAGE
 * -----
 *   import { log } from '@/lib/logger';
 *   log.info('pyn.add.ok', { invoiceId, bookId, durationMs });
 *   log.error('booking.save_failed', { invoiceId, err });   // pass Errors raw
 *
 * Event names are short dotted strings (`subsystem.action[.outcome]`) so they're
 * easy to filter: `journalctl -u daniel-cms-dev | grep '"evt":"pyn.'`.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export type LogFields = Record<string, unknown>;

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

/**
 * Threshold resolved once at module load. `LOG_LEVEL` wins when set to a known
 * level; otherwise default to `info` in production and `debug` everywhere else
 * (dev laptop, staging) so local debugging stays verbose without env fiddling.
 */
function resolveThreshold(): number {
  const raw = (process.env.LOG_LEVEL || '').trim().toLowerCase();
  if (raw && raw in LEVEL_ORDER) return LEVEL_ORDER[raw as LogLevel];
  return process.env.NODE_ENV === 'production'
    ? LEVEL_ORDER.info
    : LEVEL_ORDER.debug;
}

const THRESHOLD = resolveThreshold();

// Field-level + line-level size caps. MAX_FIELD bounds any single string (PYN
// body, SQL error, stack trace); MAX_LINE is the absolute ceiling for one line.
const MAX_FIELD = 2000;
const MAX_LINE = 16_000;

// Case-insensitive match on the KEY name (never the value) — anything that looks
// like a credential is replaced wholesale. Covers PYN client_secret/access_token,
// EuPlatesc keys, Authorization headers, Supabase service keys, cookies.
const SECRET_KEY_RE =
  /pass(word)?|secret|token|authorization|api[-_]?key|client_secret|access_token|cookie|credential|priv(ate)?_?key/i;

function capString(s: string): string {
  return s.length > MAX_FIELD
    ? `${s.slice(0, MAX_FIELD)}…[+${s.length - MAX_FIELD}]`
    : s;
}

/**
 * A fresh replacer per emit (the WeakSet for circular-ref detection must not be
 * shared across calls). Handles secrets, Error objects, oversized strings,
 * bigints, and circular references — so `JSON.stringify` can't throw on them.
 */
function buildReplacer(): (this: unknown, key: string, value: unknown) => unknown {
  const seen = new WeakSet<object>();
  return function (key, value) {
    if (key && SECRET_KEY_RE.test(key)) return '[redacted]';
    if (value instanceof Error) {
      return {
        name: value.name,
        message: capString(value.message || ''),
        stack: value.stack ? capString(value.stack) : undefined,
      };
    }
    if (typeof value === 'string') return capString(value);
    if (typeof value === 'bigint') return value.toString();
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return '[circular]';
      seen.add(value);
    }
    return value;
  };
}

function nowIso(): string {
  // App runtime (not a workflow script) — Date is available and fine here.
  return new Date().toISOString();
}

// ── Durable file sink (Coolify persistent volume) ───────────────────────────
// Opt-in via LOG_DIR. Additive to stdout; survives container recreation when
// LOG_DIR points at a persistent volume. Config resolved once at module load.
const LOG_DIR = (process.env.LOG_DIR || '').trim();
const LOG_FILE_NAME = 'app.ndjson';
const LOG_FILE_MAX_BYTES =
  Number(process.env.LOG_FILE_MAX_BYTES) > 0
    ? Number(process.env.LOG_FILE_MAX_BYTES)
    : 50_000_000; // 50 MB per file
const LOG_FILE_KEEP =
  Number(process.env.LOG_FILE_KEEP) >= 1 ? Number(process.env.LOG_FILE_KEEP) : 5;

type FsModule = typeof import('node:fs');
let fsMod: FsModule | null | undefined;

// Acquire `fs` lazily and ONLY on a real Node runtime. The logger is imported by
// 'use client' components and edge middleware; a static `import 'node:fs'` would
// break those bundles. `process.getBuiltinModule` (Node 22+) hands back a builtin
// synchronously with no static import for a bundler to resolve, and is undefined
// on the client/edge — so the guard below bails there and the sink stays off.
function getFs(): FsModule | null {
  if (fsMod !== undefined) return fsMod;
  try {
    const proc = typeof process !== 'undefined' ? process : undefined;
    const getBuiltin = (
      proc as unknown as { getBuiltinModule?: (id: string) => unknown } | undefined
    )?.getBuiltinModule;
    if (typeof getBuiltin !== 'function') {
      fsMod = null;
      return null;
    }
    fsMod = getBuiltin.call(proc, 'node:fs') as FsModule;
  } catch {
    fsMod = null;
  }
  return fsMod;
}

let fileSink: { fd: number; size: number } | null = null;
// Disabled from the start when unconfigured; also latched on first failure so a
// broken mount can never spam or slow the hot path.
let fileSinkDisabled = LOG_DIR === '';

function rotateSink(fs: FsModule): void {
  if (fileSink) {
    try {
      fs.closeSync(fileSink.fd);
    } catch {
      /* ignore */
    }
    fileSink = null;
  }
  const base = `${LOG_DIR}/${LOG_FILE_NAME}`;
  try {
    fs.rmSync(`${base}.${LOG_FILE_KEEP}`, { force: true });
  } catch {
    /* ignore */
  }
  for (let i = LOG_FILE_KEEP - 1; i >= 1; i--) {
    try {
      if (fs.existsSync(`${base}.${i}`)) fs.renameSync(`${base}.${i}`, `${base}.${i + 1}`);
    } catch {
      /* ignore */
    }
  }
  try {
    if (fs.existsSync(base)) fs.renameSync(base, `${base}.1`);
  } catch {
    /* ignore */
  }
}

function appendToFile(line: string): void {
  if (fileSinkDisabled) return;
  const fs = getFs();
  if (!fs) {
    fileSinkDisabled = true;
    return;
  }
  try {
    if (!fileSink) {
      fs.mkdirSync(LOG_DIR, { recursive: true });
      const full = `${LOG_DIR}/${LOG_FILE_NAME}`;
      const fd = fs.openSync(full, 'a');
      let size = 0;
      try {
        size = fs.statSync(full).size;
      } catch {
        /* fresh file */
      }
      fileSink = { fd, size };
    }
    const written = fs.writeSync(fileSink.fd, `${line}\n`);
    fileSink.size += written;
    if (fileSink.size >= LOG_FILE_MAX_BYTES) rotateSink(fs);
  } catch {
    // Latch off on first failure — never retry-throw per line, never block flow.
    fileSinkDisabled = true;
    if (fileSink) {
      try {
        fs.closeSync(fileSink.fd);
      } catch {
        /* ignore */
      }
      fileSink = null;
    }
  }
}

function writeLine(level: LogLevel, line: string): void {
  const useErr = level === 'warn' || level === 'error';
  // Server path: write NDJSON straight to the std streams (→ journald / Docker).
  const proc: NodeJS.Process | undefined =
    typeof process !== 'undefined' ? process : undefined;
  const stream = useErr ? proc?.stderr : proc?.stdout;
  if (stream && typeof stream.write === 'function') {
    stream.write(`${line}\n`);
    appendToFile(line); // durable copy when LOG_DIR is configured; else no-op
    return;
  }
  // Browser / no std streams: fall back to console with the same structured line.
  /* eslint-disable no-console */
  if (level === 'error') console.error(line);
  else if (level === 'warn') console.warn(line);
  else console.log(line);
  /* eslint-enable no-console */
}

function emit(level: LogLevel, evt: string, fields?: LogFields): void {
  try {
    if (LEVEL_ORDER[level] < THRESHOLD) return; // cheap gate — no serialization

    const record: LogFields = { t: nowIso(), lvl: level, evt, ...(fields || {}) };

    let line: string;
    try {
      line = JSON.stringify(record, buildReplacer());
    } catch {
      // Last-ditch: never let a serialization failure swallow the event entirely.
      line = JSON.stringify({ t: nowIso(), lvl: level, evt, _serializeError: true });
    }
    if (line.length > MAX_LINE) {
      line = JSON.stringify({ t: nowIso(), lvl: level, evt, _truncatedBytes: line.length });
    }
    writeLine(level, line);
  } catch {
    // The whole point: logging can never break the caller.
  }
}

export const log = {
  debug: (evt: string, fields?: LogFields) => emit('debug', evt, fields),
  info: (evt: string, fields?: LogFields) => emit('info', evt, fields),
  warn: (evt: string, fields?: LogFields) => emit('warn', evt, fields),
  error: (evt: string, fields?: LogFields) => emit('error', evt, fields),
};

/**
 * Start a monotonic timer; the returned fn gives elapsed whole milliseconds.
 * Use to attach `durationMs` to integration calls (PYN, EuPlatesc, SMTP).
 *
 *   const elapsed = startTimer();
 *   ...await pyn...
 *   log.info('pyn.add.ok', { durationMs: elapsed() });
 */
export function startTimer(): () => number {
  const start =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : Date.now();
  return () => {
    const end =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now();
    return Math.round(end - start);
  };
}
